// Interactive MAO % slider + Smart Rehab Estimator (big-ticket toggles + cosmetic $/sqft slider)
// Replaces the static Repair Scope and MAO Calculator sections on the Underwriting page.
import React, { useState, useEffect, useMemo } from 'react';

const BIG_TICKET = [
  { key: 'roof',        label: 'Roof',         default: 15000 },
  { key: 'hvac',        label: 'HVAC / AC',    default: 12000 },
  { key: 'plumbing',    label: 'Plumbing',     default: 8000 },
  { key: 'electrical',  label: 'Electrical',   default: 7500 },
  { key: 'foundation',  label: 'Foundation',   default: 20000 },
];
const COSMETIC_DEFAULT_RATE = 50;

function fmtMoney(n) { return n != null ? '$' + Number(n).toLocaleString() : '—'; }
function fmtK(n) { return n ? '$' + Math.round(n / 1000) + 'k' : '—'; }

export default function InteractiveDealCalc({ deal, arv: initialArv, onChange }) {
  const sqft = deal.sqft || 1500;
  const zip  = deal.zip || '';
  const address = deal.address || '';

  // ── MAO % state ────────────────────────────────────────────────────────────
  const [maoPct, setMaoPct] = useState(80);
  const [maoSliderOpen, setMaoSliderOpen] = useState(false);
  const [savedMaoPct, setSavedMaoPct] = useState(null);
  const [saveAsDefault, setSaveAsDefault] = useState(false);

  // ── Big-ticket items state ─────────────────────────────────────────────────
  const [bigTicket, setBigTicket] = useState(
    BIG_TICKET.reduce((acc, item) => {
      acc[item.key] = { flagged: false, amount: item.default };
      return acc;
    }, {})
  );

  // ── Cosmetic state ─────────────────────────────────────────────────────────
  const [sqftRate, setSqftRate] = useState(COSMETIC_DEFAULT_RATE);
  const [savedSqftRate, setSavedSqftRate] = useState(null);
  const [savedNeighborhood, setSavedNeighborhood] = useState(null);
  const [showSavePrompt, setShowSavePrompt] = useState(false);
  const [pendingSqftRate, setPendingSqftRate] = useState(null);
  const [cosmeticSliderOpen, setCosmeticSliderOpen] = useState(false);

  // Load saved preferences for this zip / neighborhood
  useEffect(() => {
    if (!zip) return;
    fetch(`/api/preferences/mao?zip=${zip}`).then(r => r.json()).then(d => {
      if (d.mao_pct) {
        setSavedMaoPct(d.mao_pct);
        setMaoPct(d.mao_pct);
      }
    }).catch(() => {});

    fetch(`/api/preferences/rehab?zip=${zip}&address=${encodeURIComponent(address)}`).then(r => r.json()).then(d => {
      if (d.sqft_rate) {
        setSavedSqftRate(d.sqft_rate);
        setSqftRate(d.sqft_rate);
        setSavedNeighborhood(d.detected_neighborhood);
      }
    }).catch(() => {});
  }, [zip, address]);

  // Calculate totals
  const bigTicketTotal = useMemo(
    () => Object.entries(bigTicket).reduce((sum, [k, v]) => sum + (v.flagged ? (parseInt(v.amount) || 0) : 0), 0),
    [bigTicket]
  );
  const cosmeticTotal = sqft * sqftRate;
  const rehabTotal    = bigTicketTotal + cosmeticTotal;
  const mao           = Math.round(initialArv * (maoPct / 100) - rehabTotal);

  // Notify parent of changes
  useEffect(() => {
    if (onChange) onChange({ rehabTotal, maoPct, mao });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rehabTotal, maoPct, mao]);

  function toggleBigTicket(key) {
    setBigTicket(prev => ({ ...prev, [key]: { ...prev[key], flagged: !prev[key].flagged } }));
  }
  function updateBigTicketAmount(key, amount) {
    setBigTicket(prev => ({ ...prev, [key]: { ...prev[key], amount } }));
  }

  function commitSqftRate(newRate) {
    if (newRate === sqftRate) return;
    setPendingSqftRate(newRate);
    setSqftRate(newRate);
    setShowSavePrompt(true);
  }

  async function saveMao() {
    if (!saveAsDefault || !zip) { setMaoSliderOpen(false); return; }
    try {
      await fetch('/api/preferences/mao', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ zip, mao_pct: maoPct }),
      });
      setSavedMaoPct(maoPct);
    } catch {}
    setSaveAsDefault(false);
    setMaoSliderOpen(false);
  }

  async function saveSqftPreference(yes) {
    if (yes && pendingSqftRate) {
      try {
        const r = await fetch('/api/preferences/rehab', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ zip, address, sqft_rate: pendingSqftRate }),
        });
        const data = await r.json();
        setSavedSqftRate(pendingSqftRate);
        if (data.neighborhood) setSavedNeighborhood(data.neighborhood);
      } catch {}
    }
    setShowSavePrompt(false);
    setPendingSqftRate(null);
  }

  return (
    <>
      {/* ── REPAIR / REHAB ESTIMATOR ───────────────────────────────────────── */}
      <Section title="Smart Rehab Estimator">
        <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
          <Stat label="Total Rehab" value={fmtK(rehabTotal)} color="var(--gold)" />
          <Stat label="Big Ticket"  value={fmtK(bigTicketTotal)} color={bigTicketTotal ? '#B85450' : 'var(--gray-300)'} />
          <Stat label="Cosmetic"    value={fmtK(cosmeticTotal)} color="var(--gray-300)" />
        </div>

        {/* Big ticket toggles */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
            Big Ticket Items
          </div>
          {BIG_TICKET.map(item => {
            const state = bigTicket[item.key];
            return (
              <div key={item.key} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: '1px solid rgba(232, 227, 218, 0.7)' }}>
                <div style={{ flex: 1, fontSize: 13, color: 'var(--gray-300)', fontWeight: 500 }}>{item.label}</div>
                <ToggleGoodNeedsWork
                  flagged={state.flagged}
                  onToggle={() => toggleBigTicket(item.key)}
                />
                {state.flagged && (
                  <input
                    type="number"
                    value={state.amount}
                    onChange={e => updateBigTicketAmount(item.key, parseInt(e.target.value) || 0)}
                    style={{ width: 100, textAlign: 'right', padding: '5px 8px', fontSize: 12, color: 'var(--white)', background: 'var(--navy)', border: '1px solid var(--navy-border)', borderRadius: 6, outline: 'none' }}
                  />
                )}
                {!state.flagged && <span style={{ fontSize: 11, color: 'var(--gray-500)', width: 100, textAlign: 'right' }}>—</span>}
              </div>
            );
          })}
          <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 10, fontSize: 12 }}>
            <span style={{ color: 'var(--gray-500)' }}>Big Ticket Subtotal</span>
            <span style={{ color: '#B85450', fontWeight: 700 }}>{fmtMoney(bigTicketTotal)}</span>
          </div>
        </div>

        {/* Cosmetic */}
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
            Cosmetic Updates
          </div>
          <div style={{ background: 'var(--navy)', borderRadius: 8, padding: 14 }}>
            <div style={{ fontSize: 14, color: 'var(--gray-300)' }}>
              {sqft.toLocaleString()} sqft × <button onClick={() => setCosmeticSliderOpen(o => !o)} style={{ background: 'none', border: 'none', color: 'var(--teal)', fontWeight: 700, cursor: 'pointer', fontSize: 14, padding: 0, textDecoration: 'underline' }}>${sqftRate}/sqft</button> = <span style={{ color: 'var(--white)', fontWeight: 700 }}>{fmtMoney(cosmeticTotal)}</span>
            </div>
            {savedSqftRate && (
              <div style={{ fontSize: 10, color: 'var(--teal)', marginTop: 4, fontWeight: 500 }}>
                Based on your saved rate for {savedNeighborhood || zip}: ${savedSqftRate}/sqft — tap to change
              </div>
            )}
            {cosmeticSliderOpen && (
              <div style={{ marginTop: 12 }}>
                <input
                  type="range" min={25} max={120} step={1}
                  value={sqftRate}
                  onChange={e => setSqftRate(parseInt(e.target.value))}
                  onMouseUp={() => commitSqftRate(sqftRate)}
                  onTouchEnd={() => commitSqftRate(sqftRate)}
                  style={{ width: '100%', accentColor: 'var(--teal)' }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--gray-500)' }}>
                  <span>$25</span><span style={{ color: 'var(--teal)', fontWeight: 700 }}>${sqftRate}/sqft</span><span>$120</span>
                </div>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 10, fontSize: 12 }}>
            <span style={{ color: 'var(--gray-500)' }}>Cosmetic Subtotal</span>
            <span style={{ color: 'var(--gray-300)', fontWeight: 700 }}>{fmtMoney(cosmeticTotal)}</span>
          </div>
        </div>

        <div style={{ borderTop: '1px solid var(--navy-border)', marginTop: 14, paddingTop: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--white)' }}>Total Rehab</span>
          <span style={{ fontSize: 22, fontWeight: 700, color: 'var(--gold)' }}>{fmtMoney(rehabTotal)}</span>
        </div>

        {/* Manual save prompt */}
        {showSavePrompt && pendingSqftRate && (
          <div style={{ marginTop: 14, padding: 14, background: 'rgba(20, 80, 74,0.06)', border: '1px solid rgba(20, 80, 74,0.3)', borderRadius: 10 }}>
            <div style={{ fontSize: 12, color: 'var(--gray-300)', marginBottom: 10 }}>
              You changed the rehab rate to <strong style={{ color: 'var(--teal)' }}>${pendingSqftRate}/sqft</strong> for this deal.
              Would you like to make ${pendingSqftRate}/sqft your default for {savedNeighborhood || (zip ? `zip ${zip}` : 'this area')}?
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => saveSqftPreference(true)} style={btnSolid('var(--teal)')}>Yes, save as default</button>
              <button onClick={() => saveSqftPreference(false)} style={btnGhost()}>No, just this deal</button>
            </div>
          </div>
        )}
      </Section>

      {/* ── MAO CALCULATOR ─────────────────────────────────────────────────── */}
      <Section title="Max Allowable Offer">
        <div style={{ background: 'rgba(20, 80, 74,0.08)', borderRadius: 10, padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 4 }}>Recommended MAO</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--teal)' }}>{fmtMoney(mao)}</div>
          <div style={{ marginTop: 10, fontSize: 13, color: 'var(--gray-300)', fontFamily: 'monospace' }}>
            ARV × <button onClick={() => setMaoSliderOpen(o => !o)} style={{ background: 'none', border: 'none', color: 'var(--teal)', fontWeight: 700, cursor: 'pointer', fontSize: 14, padding: 0, textDecoration: 'underline' }}>{maoPct}%</button> − rehab = MAO
          </div>
          {savedMaoPct && savedMaoPct !== 80 && (
            <div style={{ fontSize: 10, color: 'var(--teal)', marginTop: 4, fontWeight: 500 }}>
              Your default for {zip}: {savedMaoPct}%
            </div>
          )}

          {maoSliderOpen && (
            <div style={{ marginTop: 14, padding: 12, background: 'var(--navy)', borderRadius: 8 }}>
              <input
                type="range" min={65} max={90} step={1}
                value={maoPct}
                onChange={e => setMaoPct(parseInt(e.target.value))}
                style={{ width: '100%', accentColor: 'var(--teal)' }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--gray-500)', marginBottom: 12 }}>
                <span>65% (conservative)</span>
                <span style={{ color: 'var(--teal)', fontWeight: 700, fontSize: 12 }}>{maoPct}%</span>
                <span>90% (aggressive)</span>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12, color: 'var(--gray-300)' }}>
                <input type="checkbox" checked={saveAsDefault} onChange={e => setSaveAsDefault(e.target.checked)} />
                Save as default for zip {zip || '(no zip)'}
              </label>
              <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                <button onClick={saveMao} style={{ ...btnSolid('var(--teal)'), flex: 1 }}>Save</button>
                <button onClick={() => { setMaoSliderOpen(false); setSaveAsDefault(false); }} style={{ ...btnGhost(), flex: 1 }}>Cancel</button>
              </div>
            </div>
          )}
        </div>

        <Row label="ARV" value={fmtMoney(initialArv)} />
        <Row label={`× ${maoPct}%`} value={fmtMoney(Math.round(initialArv * maoPct / 100))} />
        <Row label="− Rehab" value={'(' + fmtMoney(rehabTotal) + ')'} color="#B85450" />
        <Row label="= MAO" value={fmtMoney(mao)} color="var(--teal)" bold />

        {deal.asking_price && (
          <div style={{ marginTop: 14, padding: '10px 14px', borderRadius: 8, background: mao >= deal.asking_price ? 'rgba(74, 124, 89,0.08)' : 'rgba(224, 165, 38,0.08)', border: `1px solid ${mao >= deal.asking_price ? '#4A7C5940' : '#E0A52640'}` }}>
            <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 3 }}>List vs MAO</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: mao >= deal.asking_price ? '#4A7C59' : '#E0A526' }}>
              {mao >= deal.asking_price
                ? `✓ Under MAO by ${fmtMoney(mao - deal.asking_price)}`
                : `${fmtMoney(deal.asking_price - mao)} above MAO — negotiate down`}
            </div>
          </div>
        )}
      </Section>
    </>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function Section({ title, children }) {
  return (
    <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 12, padding: '24px', marginBottom: 20 }}>
      <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--white)', marginBottom: 20, textTransform: 'uppercase', letterSpacing: '0.06em', opacity: 0.85 }}>{title}</h3>
      {children}
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div style={{ flex: 1, background: 'var(--navy)', borderRadius: 8, padding: 12, textAlign: 'center', border: '1px solid var(--navy-border)' }}>
      <div style={{ fontSize: 18, fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 2 }}>{label}</div>
    </div>
  );
}

function Row({ label, value, color = 'var(--gray-300)', bold }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid rgba(232, 227, 218, 0.7)', fontFamily: 'monospace' }}>
      <span style={{ fontSize: 12, color: bold ? 'var(--white)' : 'var(--gray-500)', fontWeight: bold ? 700 : 400 }}>{label}</span>
      <span style={{ fontSize: bold ? 15 : 13, color, fontWeight: bold ? 700 : 600 }}>{value}</span>
    </div>
  );
}

function ToggleGoodNeedsWork({ flagged, onToggle }) {
  return (
    <button onClick={onToggle} style={{
      display: 'flex', border: '1px solid var(--navy-border)', borderRadius: 6, overflow: 'hidden',
      background: 'var(--navy)', padding: 0, cursor: 'pointer',
    }}>
      <span style={{
        padding: '4px 10px', fontSize: 10, fontWeight: 700,
        background: !flagged ? '#4A7C59' : 'transparent',
        color: !flagged ? '#fff' : 'var(--gray-500)',
      }}>Good</span>
      <span style={{
        padding: '4px 10px', fontSize: 10, fontWeight: 700,
        background: flagged ? '#B85450' : 'transparent',
        color: flagged ? '#fff' : 'var(--gray-500)',
      }}>Needs Work</span>
    </button>
  );
}

function btnSolid(color) { return { padding: '7px 14px', borderRadius: 6, background: color, border: 'none', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }; }
function btnGhost() { return { padding: '7px 14px', borderRadius: 6, background: 'transparent', border: '1px solid var(--navy-border)', color: 'var(--gray-500)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }; }
